
import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../services/firebase';
import { Search } from 'lucide-react';
import { getSportIcon } from '../../../utils/sportsUtils';
import { SPORTS_CONFIG, WORLD_CUP_SPORT } from '../../../utils/constants';
import { useLanguage } from '../../../context/LanguageContext';
import { useAuth } from '../../../context/AuthContext';
import { useBetting } from '../../../context/BettingContext';
import { useLiveEvents } from '../../../context/LiveEventsContext';
import SEO from '../../../components/SEO/SEO';
import { processEvent } from '../../../utils/eventUtils';

// Subcomponents - LAZY LOADED for Performance
import LobbyTicker from '../components/LobbyTicker';
import LobbyBanners from '../components/LobbyBanners';
import LobbyLiveEvents from '../components/LobbyLiveEvents';
import { Loader2, Trophy, Filter, Calendar } from 'lucide-react';
import { getFlagUrl, getProxyImage, getSelectionId } from '../../../utils/formatters';
import { isMatchLive, translateSportName } from '../../../utils/sportsUtils';

import { useNavigate } from 'react-router-dom';

const LobbyCasino = lazy(() => import('../components/LobbyCasino'));
const LobbySpaceCrash = lazy(() => import('../components/LobbySpaceCrash'));
const AgendaDeportiva = lazy(() => import('../components/AgendaDeportiva'));

const BettingLobby = () => {
    const { t } = useLanguage();
    const { userProfile } = useAuth();
    const { activeSport, setActiveSport } = useLiveEvents();
    const { betSlip, addSelection } = useBetting();
    const navigate = useNavigate();

    // 1. STATE Management
    const [fetchedLiveEvents, setFetchedLiveEvents] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);

    // Search State
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    const onMatchClick = useCallback((id) => navigate(`/match/${id}`), [navigate]);
    const onOddClick = useCallback((match, market, outcome) => addSelection(match, market, outcome), [addSelection]);

    // --- Optimization: Simple Ticker Data Generation (Non-blocking) ---
    const tickerData = useMemo(() => {
        const names = ['Juan', 'Carlos', 'Ana', 'Luis', 'Sofia', 'Miguel', 'Pedro', 'Maria', 'Alejandro', 'Valentina'];
        const games_list = ['Mines', 'Aviator', 'Fortune Tiger', 'Roulette', 'Blackjack', 'Space Crash'];
        const items = [];
        for (let i = 0; i < 8; i++) {
            const name = names[Math.floor(Math.random() * names.length)];
            const prefix = name.substring(0, 3).toLowerCase();
            const email = `${prefix}***@gmail.com`;
            const game = games_list[Math.floor(Math.random() * games_list.length)];
            const amount = Math.floor(Math.random() * (3000 - 50 + 1)) + 50;
            items.push({ email, game, amount });
        }
        return items;
    }, []); // Computed once on mount

    // --- Optimization: Live Events Fetching (STRICT ALLOWLIST) ---
    useEffect(() => {
        let isMounted = true;

        const fetchLive = async () => {
            try {
                // Use a lighter query if possible
                const fn = httpsCallable(functions, 'getEvents');
                const res = await fn({ type: 'live' });

                if (!isMounted) return;

                if (res.data?.summaries) {
                    // Optimized Processing using utility
                    const validMatches = res.data.summaries
                        .map(processEvent) // Run heavy processing once here
                        .filter(Boolean); // Remote null/invalid

                    setFetchedLiveEvents(validMatches);
                }
            } catch (error) {
                console.error("Error fetching lobby live events:", error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchLive();
        const interval = setInterval(fetchLive, 60000); // 60s Auto-Refresh
        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, []);

    // --- Search Effect (Debounced) ---
    useEffect(() => {
        if (!searchQuery || searchQuery.length < 3) {
            setIsSearching(false);
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        setSearchLoading(true);

        const timer = setTimeout(async () => {
            try {
                const fn = httpsCallable(functions, 'getEvents');
                const res = await fn({ type: 'prematch', search: searchQuery });
                if (res.data?.summaries) {
                    setSearchResults(res.data.summaries.map(processEvent));
                } else {
                    setSearchResults([]);
                }
            } catch (e) {
                console.error("Search error:", e);
                setSearchResults([]);
            } finally {
                setSearchLoading(false);
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // --- Client-Side Filtering ---
    const filteredSearchResults = useMemo(() => {
        if (!searchQuery || searchResults.length === 0) return [];
        const q = searchQuery.toLowerCase();
        return searchResults.filter(e => {
            return (
                e.competitors?.[0]?.name?.toLowerCase().includes(q) ||
                e.competitors?.[1]?.name?.toLowerCase().includes(q) ||
                e.league?.toLowerCase().includes(q) ||
                (e.sportName && e.sportName.toLowerCase().includes(q))
            );
        });
    }, [searchResults, searchQuery]);

    const categories = useMemo(() => [
        // VIP Mundial 2026 (id 1000) SIEMPRE primera. Acceso directo a DEPORTES,
        // donde vive su data (The Odds API); el live_feed del lobby no la contiene.
        { id: 1000, label: WORLD_CUP_SPORT.name, Icon: getSportIcon(WORLD_CUP_SPORT), isSport: true },
        { id: 'all', label: 'Todos', Icon: Trophy, isSport: true }, // New "Todos" category
        ...SPORTS_CONFIG.map(s => ({
            id: s.id,
            label: s.name,
            Icon: getSportIcon(s),
            isSport: true
        })),
        { id: 'casino', label: t('lobby.casino_category'), Icon: null, isSport: false } // Icon handled in render
    ], [t]);

    return (
        <div className="pb-32 animate-in fade-in duration-500 bg-[var(--bg-main)] content-visibility-auto contain-content">
            <SEO />

            {/* 0. TICKER */}
            <LobbyTicker tickerItems={tickerData} />

            {/* 0.5 SEARCH BAR - Lightweight */}
            <div className="px-4 py-4">
                <div className="relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4 group-focus-within:text-[#F2C94C] transition-colors" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar equipo, liga o juego..."
                        className="w-full bg-[#27272a] border border-transparent rounded-full py-3.5 pl-12 pr-4 text-xs font-bold text-white placeholder:text-neutral-500 outline-none focus:bg-neutral-800 transition-colors shadow-inner"
                    />
                    {searchQuery.length > 0 && (
                        <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white font-bold px-2">
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {/* SEARCH RESULTS OR STANDARD CONTENT */}
            {isSearching ? (
                <div className="px-4 pb-24 space-y-4 animate-in fade-in duration-300">
                    <div className="flex items-center justify-between px-1 mb-2">
                        <h3 className="text-[10px] font-black text-white uppercase tracking-[0.3em] flex items-center gap-2">
                            <Search size={14} className="text-[#F59E0B]" />
                            RESULTADOS: "{searchQuery}"
                        </h3>
                        <Filter size={14} className="text-neutral-600" />
                    </div>

                    {searchLoading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <Loader2 className="w-10 h-10 text-[#F59E0B] animate-spin mb-4" />
                            <p className="text-neutral-500 font-bold uppercase text-[10px] tracking-widest">Buscando eventos...</p>
                        </div>
                    ) : filteredSearchResults.length === 0 ? (
                        <div className="text-center py-20 bg-[#252525]/40 rounded-[2.5rem] border border-dashed border-white/5">
                            <p className="text-neutral-600 font-bold uppercase text-[10px] tracking-widest italic">No se encontraron eventos</p>
                        </div>
                    ) : (
                        filteredSearchResults.map((event) => {
                            const market = event.markets?.[0];
                            const Icon = getSportIcon(event);
                            const isLive = isMatchLive(event);
                            const tournamentFlag = getFlagUrl(event.league);
                            // Using pre-processed fields from processEvent
                            const homeFlag = getFlagUrl(event.team1) || tournamentFlag;
                            const awayFlag = getFlagUrl(event.team2) || tournamentFlag;

                            const startDate = new Date(event.start_time);
                            const isToday = new Date().toDateString() === startDate.toDateString();
                            const dateStr = startDate.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }).replace('.', '');
                            const timeStr = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            const displayDate = isToday ? 'HOY' : dateStr.toUpperCase();

                            return (
                                <div key={event.id} className={`bg-[#252525] rounded-3xl p-4 border ${isLive ? 'border-[#EF4444]/30' : 'border-white/5'} hover:border-white/10 transition-all duration-300 shadow-lg relative group overflow-hidden`}>
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-white/5 to-transparent pointer-events-none"></div>

                                    {/* PROFESSIONAL HEADER - COMPACT */}
                                    <div className="flex justify-between items-start mb-3 pb-2 border-b border-white/5 relative z-10">
                                        <div className="flex flex-col gap-1">
                                            {/* Sport Label */}
                                            <div className="flex items-center gap-1.5 text-[#F59E0B] text-[9px] font-semibold uppercase tracking-wider opacity-80">
                                                <Icon size={10} />
                                                <span>{translateSportName(event.sportName) || 'Deporte'}</span>
                                            </div>

                                            {/* Tournament - CLEANER */}
                                            <div className="flex items-center gap-2">
                                                {event.tournament?.logo && <img src={getProxyImage(event.tournament.logo)} alt="" className="w-4 h-4 object-contain opacity-90" />}
                                                <span className="text-xs md:text-sm font-bold text-white uppercase tracking-wide">
                                                    {event.league || 'Torneo'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Date & Time Badge - COMPACT */}
                                        <div className="flex flex-col items-end">
                                            {isLive ? (
                                                <div className="flex items-center gap-1.5 bg-red-500/10 px-2 py-1 rounded border border-red-500/20">
                                                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
                                                    <span className="text-[9px] font-bold text-red-500 uppercase tracking-wider">En Vivo</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1.5 bg-[#171717] border border-white/5 rounded-lg px-2 py-1 shadow-sm">
                                                    <Calendar size={10} className="text-neutral-500" />
                                                    <span className="text-[9px] font-medium text-neutral-400 uppercase tracking-wide">{displayDate}</span>
                                                    <div className="w-px h-2.5 bg-white/10"></div>
                                                    <span className="text-[9px] font-bold text-[#F59E0B]">{timeStr}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div
                                        className="flex items-center justify-between mb-3 cursor-pointer active:scale-95 transition-transform"
                                        onClick={() => onMatchClick(event.id)}
                                    >
                                        <div className="flex flex-col items-center gap-2 w-[40%] text-center">
                                            <div className="w-12 h-12 rounded-2xl bg-neutral-800/50 border border-white/5 flex items-center justify-center shadow-lg group-hover:scale-105 transition-all duration-500 relative">
                                                {event.team1Logo || homeFlag ? (
                                                    <img
                                                        src={getProxyImage(event.team1Logo) || homeFlag}
                                                        alt={event.team1}
                                                        className="w-8 h-8 object-contain drop-shadow-md z-10"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : <span className="text-white/20 font-bold">{event.team1.charAt(0)}</span>}
                                            </div>
                                            <span className="font-bold text-[10px] text-neutral-200 uppercase leading-snug line-clamp-2 h-7 flex items-center">{event.team1}</span>
                                        </div>

                                        <div className="flex flex-col items-center">
                                            <span className="text-[8px] font-bold text-neutral-600 mb-1 tracking-widest">VS</span>
                                            <div className="h-px w-6 bg-neutral-800"></div>
                                        </div>

                                        <div className="flex flex-col items-center gap-2 w-[40%] text-center">
                                            <div className="w-12 h-12 rounded-2xl bg-neutral-800/50 border border-white/5 flex items-center justify-center shadow-lg group-hover:scale-105 transition-all duration-500 relative">
                                                {event.team2Logo || awayFlag ? (
                                                    <img
                                                        src={getProxyImage(event.team2Logo) || awayFlag}
                                                        alt={event.team2}
                                                        className="w-8 h-8 object-contain drop-shadow-md z-10"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : <span className="text-white/20 font-bold">{event.team2.charAt(0)}</span>}
                                            </div>
                                            <span className="font-bold text-[10px] text-neutral-200 uppercase leading-snug line-clamp-2 h-7 flex items-center">{event.team2}</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-2">
                                        {['1', 'X', '2'].map((label) => {
                                            const outcome = market?.outcomes?.find(o => {
                                                const oName = o.name?.toLowerCase() || "";
                                                if (label === '1') return oName === 'home' || o.id === '1' || oName === event.team1.toLowerCase();
                                                if (label === '2') return oName === 'away' || o.id === '2' || oName === event.team2.toLowerCase();
                                                return oName === 'draw' || oName === 'x' || o.id === 'x' || o.id === 'draw';
                                            });
                                            const odd = outcome?.odds;
                                            const selId = market ? getSelectionId(event.id, market, outcome) : null;
                                            const isActive = selId ? betSlip.has(selId) : false;

                                            return (
                                                <button
                                                    key={label}
                                                    onClick={() => odd && onOddClick(event, market, outcome)}
                                                    disabled={!odd}
                                                    className={`
                                                        h-10 flex flex-col items-center justify-center rounded-xl transition-all duration-300 border
                                                        ${isActive
                                                            ? 'bg-[#F59E0B] text-black border-[#F59E0B] font-bold'
                                                            : odd
                                                                ? 'bg-neutral-800/30 hover:bg-neutral-700 border-white/5 text-white active:scale-95'
                                                                : 'bg-[#252525]/30 border-white/5 text-neutral-700 opacity-50 cursor-not-allowed'}
                                                    `}
                                                >
                                                    <span className={`text-[7px] font-bold uppercase mb-0.5 tracking-widest ${isActive ? 'text-black/60' : 'text-neutral-500'}`}>{label}</span>
                                                    <span className="font-bold text-xs font-mono">{odd ? odd.toFixed(2) : '-'}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            ) : (
                <>
                    {/* 1. BANNERS - Heavy Media, isolated */}
                    <LobbyBanners />

                    {/* 2. CATEGORIES SELECTOR */}
                    <div className="flex gap-2 overflow-x-auto px-4 pb-2 scrollbar-hide mb-4 mask-linear-fade touch-pan-x">
                        {categories.map((cat) => {
                            const isActive = activeSport === cat.id;
                            const Icon = cat.Icon || getSportIcon({ id: 999, name: 'Casino' }); // Fallback or imported Icon logic
                            return (
                                <button
                                    key={cat.id}
                                    onClick={() => cat.id === 1000
                                        ? navigate('/sports', { state: { sportId: 1000 } })
                                        : setActiveSport(cat.id)}
                                    className={`
                                        flex items-center gap-2 px-5 py-2.5 rounded-full transition-all duration-300 font-black uppercase text-[10px] tracking-widest whitespace-nowrap shrink-0 border border-transparent
                                        ${isActive
                                            ? 'bg-[#F59E0B] text-black shadow-[0_0_15px_rgba(245,158,11,0.4)] transform scale-105'
                                            : 'bg-[#27272a] text-neutral-400 hover:bg-[#3f3f46] hover:text-white hover:border-white/10'}
                                    `}
                                >
                                    <Icon size={16} className={isActive ? 'text-black' : 'text-neutral-500 group-hover:text-white'} />
                                    {cat.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* 3. LIVE EVENTS */}
                    <LobbyLiveEvents liveEvents={fetchedLiveEvents} activeSport={activeSport} loading={loading} />

                    {/* 4. CASINO & JUEGOS */}
                    <Suspense fallback={<div className="h-96 animate-pulse bg-[#252525]/30 rounded-3xl mx-4 mb-16"></div>}>
                        <LobbyCasino />
                    </Suspense>

                    {/* 5. AGENDA DEPORTIVA (Optimized Component) */}
                    <section className="px-4 pb-24 content-visibility-auto">
                        <Suspense fallback={
                            <div className="bg-[#252525] rounded-3xl p-6 h-64 flex items-center justify-center animate-pulse">
                                <span className="text-gray-500 text-xs font-bold uppercase">Cargando Agenda...</span>
                            </div>
                        }>
                            <AgendaDeportiva />
                        </Suspense>
                    </section>
                </>
            )}
        </div>
    );
};

export default BettingLobby;
